import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, FileText, Film, Images } from "lucide-react";
import { fileUrl } from "../lib/api";

export function collectEvidence(c) {
  const side = (s) => (c.messages || []).filter((m) => m.side === s).flatMap((m) => m.evidence || []);
  const appeal = (id) => (c.appeal && c.appeal.by_id === id ? c.appeal.evidence || [] : []);
  return {
    complainant: [...(c.evidence || []), ...side("complainant"), ...appeal(c.complainant_id)].map((f) => ({ ...f, side: "complainant" })),
    respondent: [...(c.response?.evidence || []), ...side("respondent"), ...appeal(c.respondent_id)].map((f) => ({ ...f, side: "respondent" })),
  };
}

const Media = ({ f, big }) => {
  if (f.type === "image") return <img src={fileUrl(f.url)} alt={f.name} className={big ? "max-h-[78vh] max-w-full object-contain" : "w-full h-full object-cover"} />;
  if (f.type === "video") return big ? <video src={fileUrl(f.url)} controls autoPlay className="max-h-[78vh] max-w-full" /> : <span className="w-full h-full grid place-items-center bg-slate-800 text-white"><Film /></span>;
  return big ? <iframe src={fileUrl(f.url)} title={f.name} className="w-[min(90vw,900px)] h-[78vh] bg-white rounded" /> : <span className="w-full h-full grid place-items-center bg-slate-100 text-slate-500"><FileText /></span>;
};

function Column({ title, items, onOpen, testid }) {
  return (
    <div className="flex-1 min-w-0 bg-white/5 rounded-2xl p-4 overflow-y-auto" data-testid={testid}>
      <h3 className="text-white font-bold mb-3">{title} <span className="text-white/50 font-normal text-sm">({items.length})</span></h3>
      {items.length === 0 ? <p className="text-white/50 text-sm">No files provided.</p> :
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{items.map((f) => (
          <button key={f.url} onClick={() => onOpen(f)} className="aspect-[4/3] rounded-xl overflow-hidden ring-1 ring-white/10 hover:ring-[#FF8166] transition-shadow" title={f.name} data-testid={`${testid}-thumb`}><Media f={f} /></button>))}</div>}
    </div>
  );
}

export function EvidenceCompare({ c, onClose }) {
  const { complainant, respondent } = collectEvidence(c);
  const all = [...complainant, ...respondent];
  const [open, setOpen] = useState(null);
  const move = useCallback((d) => setOpen((i) => (i === null ? i : (i + d + all.length) % all.length)), [all.length]);
  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") { if (open !== null) setOpen(null); else onClose(); }
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, move, onClose]);
  const cur = open !== null ? all[open] : null;
  return (
    <div className="fixed inset-0 z-[90] bg-slate-950/95 backdrop-blur-sm flex flex-col p-4 sm:p-6" data-testid="evidence-compare">
      <div className="flex items-center justify-between mb-4 text-white"><h2 className="font-bold text-lg flex items-center gap-2"><Images size={18} />Evidence · {c.ref}</h2>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="evidence-compare-close"><X /></button></div>
      <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <Column title={`Complainant · ${c.complainant_name}`} items={complainant} onOpen={(f) => setOpen(all.indexOf(f))} testid="evidence-col-complainant" />
        <Column title={`Other party · ${c.respondent_name || "—"}`} items={respondent} onOpen={(f) => setOpen(all.indexOf(f))} testid="evidence-col-respondent" />
      </div>
      {cur && (
        <div className="fixed inset-0 z-[95] bg-black/95 flex items-center justify-center" onClick={() => setOpen(null)} data-testid="evidence-lightbox">
          <div className="absolute top-4 left-4 right-4 flex justify-between text-white text-sm"><span><b>{cur.side === "complainant" ? "Complainant" : "Other party"}</b> · {cur.name} · {open + 1}/{all.length}</span>
            <span className="flex gap-3"><a href={fileUrl(cur.url)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline">Open original</a><button onClick={() => setOpen(null)} aria-label="Close viewer" data-testid="evidence-lightbox-close"><X /></button></span></div>
          <button onClick={(e) => { e.stopPropagation(); move(-1); }} className="absolute left-3 p-3 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Previous" data-testid="evidence-prev"><ChevronLeft /></button>
          <div onClick={(e) => e.stopPropagation()}><Media f={cur} big /></div>
          <button onClick={(e) => { e.stopPropagation(); move(1); }} className="absolute right-3 p-3 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Next" data-testid="evidence-next"><ChevronRight /></button>
        </div>
      )}
    </div>
  );
}
